import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import FolderSharedIcon from '@mui/icons-material/FolderShared';

import GradientCircularProgress from '../../../../../components/common/GradientCircularProgress';
import { useThemeContext } from '../../../../../contexts/BaseThemeContextProvider';
import useBackendApi from '../../../../../hooks/useBackendApi';
import { RequestOptions } from '../../../../../types/httpRequests';
import { AccountEvent, AccountType, OrganizationStaffMember } from '../../../../../types/account';
import { AccountEventDetailsCard } from './AccountEventNameDescription';
import EventCompletionSection from './EventCompletionSection';
import EventInvoiceSection from './EventInvoiceSection';
import type { EventCompletionResponse } from './eventCompletionApi';
import { hasAdminOrMemberRole } from './roleUtils';
import { applyCalendarSyncIssue } from './calendarSyncUtils';
import { formatEventDateTime } from '../../../../../utils/dateTime';
import {
  driveIconButtonSx,
  ghostActionIconButtonSx,
} from '../../../../../components/common/buttons/actionIconButtonStyles';
import { defaultSiteTitle } from '../../../../../constants/shop';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

type RouteParams = {
  org_path_name: string;
  acc_path_name: string;
  event_id: string;
};

export default function AccountEventDetails(): JSX.Element {
  const { org_path_name, acc_path_name, event_id } = useParams<RouteParams>() as RouteParams;
  const navigate = useNavigate();
  const { theme } = useThemeContext();
  const { apiFetchRequest, isLoading } = useBackendApi();

  const [account, setAccount] = useState<AccountType | null>(null);
  const [event, setEvent] = useState<AccountEvent | null>(null);
  const [userOrgRoles, setUserOrgRoles] = useState<string[]>([]);
  const [viewerAccountRoles, setViewerAccountRoles] = useState<string[]>([]);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingStaffId, setDeletingStaffId] = useState<number | null>(null);
  const [calendarSyncError, setCalendarSyncError] = useState<string | null>(null);
  const [invoiceRefreshKey, setInvoiceRefreshKey] = useState(0);
  const guestFolderRefreshAttemptsRef = useRef(0);
  const fail = useCallback(() => setErrorText('Something went wrong. Please try again.'), []);

  const eventIdNumber = useMemo(() => {
    const parsed = Number(event_id);
    if (Number.isNaN(parsed)) {
      return null;
    }
    return parsed;
  }, [event_id]);

  const fetchEventDetails = useCallback(async () => {
    if (eventIdNumber === null) {
      return;
    }

    const opts: RequestOptions = {
      endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
        acc_path_name,
      )}/events/${encodeURIComponent(String(eventIdNumber))}`,
      method: 'GET',
    };

    try {
      const res = await apiFetchRequest(opts);

      if (!res) {
        fail();
        return;
      }

      const { body, status, isError } = res;

      const unauthorized =
        status === 401 ||
        body?.status_code === 401 ||
        body?.message === 'UNAUTHENTICATED' ||
        body?.message === 'UNAUTHORIZED' ||
        body?.message === 'NO_ACCESS';

      if (unauthorized) {
        navigate('/401.html');
        return;
      }

      if (status === 404 || body?.status_code === 404 || body?.message === 'NOT_FOUND') {
        navigate('/404.html');
        return;
      }

      if (isError || status !== 200) {
        fail();
        return;
      }

      if (body?.account && body?.event) {
        setAccount(body.account as AccountType);
        setEvent({
          ...body.event,
          viewer_org_roles: body.viewer_org_roles,
          viewer_account_roles: body.viewer_account_roles,
        } as AccountEvent);
        setUserOrgRoles(Array.isArray(body.viewer_org_roles) ? (body.viewer_org_roles as string[]) : []);
        setViewerAccountRoles(Array.isArray(body.viewer_account_roles) ? (body.viewer_account_roles as string[]) : []);
        setErrorText(null);
      } else {
        fail();
      }
    } catch {
      fail();
    }
  }, [acc_path_name, apiFetchRequest, eventIdNumber, fail, navigate, org_path_name]);

  useEffect(() => {
    if (eventIdNumber === null) {
      navigate('/404.html');
      return;
    }

    void fetchEventDetails();
  }, [acc_path_name, eventIdNumber, fetchEventDetails, navigate, org_path_name]);

  useEffect(() => {
    if (event?.title && account?.name) {
      document.title = `${event.title} | ${account.name} | Event Details | ${defaultSiteTitle}`;
    } else if (event?.title) {
      document.title = `${event.title} | Event Details | ${defaultSiteTitle}`;
    } else if (account?.name) {
      document.title = `Event Details | ${account.name} | ${defaultSiteTitle}`;
    }
  }, [event?.title, account?.name]);

  const hasOrgAdminOrMemberRole = useMemo(() => hasAdminOrMemberRole(userOrgRoles), [userOrgRoles]);

  const canManageEvents = useMemo(() => {
    if (hasOrgAdminOrMemberRole) {
      return true;
    }
    return hasAdminOrMemberRole(viewerAccountRoles);
  }, [hasOrgAdminOrMemberRole, viewerAccountRoles]);

  const canAssignStaff = hasOrgAdminOrMemberRole;
  const canViewGuestDriveFolders = hasOrgAdminOrMemberRole;
  const hasMissingGuestDriveFolders = useMemo(() => {
    if (!canViewGuestDriveFolders || !event?.guests?.length) {
      return false;
    }

    return event.guests.some((guest) => {
      if (!guest) {
        return false;
      }
      const hasGuestIdentifier = Boolean(guest.user_id || guest.patient_id || guest.email);
      return hasGuestIdentifier && !guest.storage_folder_map?.google_drive_url;
    });
  }, [canViewGuestDriveFolders, event?.guests]);

  useEffect(() => {
    guestFolderRefreshAttemptsRef.current = 0;
  }, [eventIdNumber]);

  useEffect(() => {
    if (!hasMissingGuestDriveFolders) {
      guestFolderRefreshAttemptsRef.current = 0;
      return;
    }

    if (guestFolderRefreshAttemptsRef.current >= 5) {
      return;
    }

    const refreshTimeout = window.setTimeout(() => {
      guestFolderRefreshAttemptsRef.current += 1;
      void fetchEventDetails();
    }, 1500);

    return () => window.clearTimeout(refreshTimeout);
  }, [fetchEventDetails, hasMissingGuestDriveFolders]);

  const handleEditEvent = useCallback(() => {
    if (!event?.acc_event_id) {
      return;
    }

    navigate(
      `/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(acc_path_name)}/events/${encodeURIComponent(
        String(event.acc_event_id),
      )}/edit`,
      { state: { event, viewer_org_roles: userOrgRoles } },
    );
  }, [acc_path_name, event, navigate, org_path_name, userOrgRoles]);

  const handleDeleteAssignedStaff = useCallback(
    async (member: OrganizationStaffMember) => {
      if (!canAssignStaff || eventIdNumber === null) {
        return;
      }

      const staffId = member.org_staff_id;
      if (typeof staffId !== 'number') {
        return;
      }

      setDeletingStaffId(staffId);

      try {
        const response = await apiFetchRequest({
          endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
            acc_path_name,
          )}/events/${encodeURIComponent(String(eventIdNumber))}/staff/${encodeURIComponent(String(staffId))}`,
          method: 'DELETE',
        });

        if (!response) {
          fail();
          return;
        }

        const { status, body, isError } = response;

        const unauthorized =
          status === 401 ||
          body?.status_code === 401 ||
          body?.message === 'UNAUTHENTICATED' ||
          body?.message === 'UNAUTHORIZED' ||
          body?.message === 'NO_ACCESS';

        if (unauthorized) {
          navigate('/401.html');
          return;
        }

        if (status === 404 || body?.status_code === 404 || body?.message === 'NOT_FOUND') {
          return;
        }

        const handled = await applyCalendarSyncIssue(response, {
          onMessage: setCalendarSyncError,
        });
        if (handled) {
          return;
        }

        if (isError || status !== 200) {
          fail();
          return;
        }

        await fetchEventDetails();
      } catch {
        fail();
      } finally {
        setDeletingStaffId(null);
      }
    },
    [
      acc_path_name,
      apiFetchRequest,
      canAssignStaff,
      eventIdNumber,
      fail,
      fetchEventDetails,
      navigate,
      org_path_name,
      setCalendarSyncError,
    ],
  );

  const handleDeleteEvent = useCallback(async () => {
    if (eventIdNumber === null) {
      return;
    }

    const confirmed = window.confirm('Are you sure you want to delete this event? This action cannot be undone.');
    if (!confirmed) {
      return;
    }

    setCalendarSyncError(null);
    setIsDeleting(true);

    try {
      const response = await apiFetchRequest({
        endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
          acc_path_name,
        )}/events/${encodeURIComponent(String(eventIdNumber))}`,
        method: 'DELETE',
      });

      if (!response) {
        fail();
        return;
      }

      const handled = await applyCalendarSyncIssue(response, {
        onMessage: setCalendarSyncError,
      });
      if (handled) {
        return;
      }

      if (response.isError || (response.status !== 200 && response.status !== 204)) {
        fail();
        return;
      }

      navigate(`/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(acc_path_name)}/events/`);
    } catch {
      fail();
    } finally {
      setIsDeleting(false);
    }
  }, [acc_path_name, apiFetchRequest, eventIdNumber, fail, navigate, org_path_name, setCalendarSyncError]);

  const handleEventCompleted = useCallback(
    async (response: EventCompletionResponse) => {
      setEvent((current) =>
        current
          ? {
              ...current,
              event_completion_status: response.event_completion_status,
              event_completed_at: response.event_completed_at,
              event_completed_by_user_id: response.event_completed_by_user_id,
            }
          : current,
      );
      await fetchEventDetails();
      setInvoiceRefreshKey((current) => current + 1);
    },
    [fetchEventDetails],
  );

  if ((isLoading && !account && !errorText) || eventIdNumber === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', height: '100vh' }}>
        <GradientCircularProgress />
      </Box>
    );
  }

  if (errorText) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', px: 2 }}>
        <Typography color="error">{errorText}</Typography>
      </Box>
    );
  }

  if (!account || !event) {
    return <></>;
  }

  const driveFolderButtonSx = driveIconButtonSx(theme.buttons.ghost.color, theme.buttons.ghost.hover.color);

  const title = event.title?.trim() || 'Untitled Event';
  const dateStr = formatEventDateTime(event.event_start_date, event.event_end_date);

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'transparent',
          padding: '0px 40px',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 4,
          flexWrap: 'wrap',
          mt: 0,
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
            }}
          >
            {title}
          </Typography>
          {dateStr && dateStr !== '-' && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mt: 0.5,
              }}
            >
              {dateStr}
            </Typography>
          )}
        </Box>

        {canManageEvents && (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            {hasAdminOrMemberRole(userOrgRoles) && event?.storage_folder_map?.google_drive_url && (
              <Tooltip title="View Drive Folder" arrow>
                <span>
                  <IconButton
                    onClick={() =>
                      window.open(event.storage_folder_map!.google_drive_url, '_blank', 'noopener,noreferrer')
                    }
                    sx={driveFolderButtonSx}
                  >
                    <FolderSharedIcon sx={{ fontSize: '24px' }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Edit Event" arrow>
              <span>
                <IconButton
                  aria-label="Edit Event"
                  onClick={handleEditEvent}
                  sx={ghostActionIconButtonSx(theme.images.editIcon)}
                />
              </span>
            </Tooltip>
            <Tooltip title="Delete Event" arrow>
              <span>
                <IconButton
                  aria-label="Delete Event"
                  onClick={handleDeleteEvent}
                  disabled={isDeleting}
                  sx={ghostActionIconButtonSx(theme.images.deleteIcon)}
                >
                  {isDeleting && <CircularProgress size={24} color="inherit" />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}
      </Box>

      {calendarSyncError ? (
        <Box sx={{ px: '40px', mb: 4 }}>
          <Typography color="error">{calendarSyncError}</Typography>
        </Box>
      ) : null}
      <Box sx={{ mt: 3 }}>
        <AccountEventDetailsCard
          event={event}
          onDeleteAssignedStaff={canAssignStaff ? handleDeleteAssignedStaff : undefined}
          deletingStaffId={deletingStaffId}
          canViewGuestDriveFolders={canViewGuestDriveFolders}
          canMoveEmployees={hasOrgAdminOrMemberRole}
          onRequirementsUpdated={fetchEventDetails}
        />
      </Box>
      {hasOrgAdminOrMemberRole ? (
        <Box sx={{ mt: 3 }}>
          <EventCompletionSection
            event={event}
            orgPathName={org_path_name}
            accountPathName={acc_path_name}
            onCompleted={handleEventCompleted}
          />
        </Box>
      ) : null}
      {hasOrgAdminOrMemberRole ? (
        <Box sx={{ mt: 3 }}>
          <EventInvoiceSection
            event={event}
            orgPathName={org_path_name}
            accountPathName={acc_path_name}
            refreshKey={invoiceRefreshKey}
          />
        </Box>
      ) : null}
    </>
  );
}
